import React, { ReactNode, useMemo } from 'react'

import { faTimes } from '@fortawesome/pro-light-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  Badge,
  Center,
  type SelectItem,
  Tooltip,
  useMantineTheme,
} from '@mantine/core'
import dayjs from 'dayjs'
import { find } from 'lodash-es'

import { isNotNull } from '../../../../utils'
import {
  SORT_FILTER_TRAIT_DATERANGE,
  SORT_FILTER_TRAIT_MULTIVALUE,
  SORT_FILTER_TRAIT_THREE_SIDED_BOOLEAN,
  SORT_FILTER_TRAIT_UNRESTRICTED_VALUE,
  SORT_FILTER_TRAIT_VALUE,
} from '../../constants'
import {
  arraySlicer,
  getResetValue,
  isValidDateRangeFilter,
} from '../../hooks/useDebouncedFilterValue/utils'
import { UseSortFilter } from '../../hooks/useSortFilter/use-sort-filter'
import { FilterChipProps } from '../filter-chips'

const FilterChip = ({
  filterDefinition,
  activeFilters,
  setFilterValue,
}: FilterChipProps): React.ReactElement => {
  const matchedFilter = useMemo(() => {
    return activeFilters.find((f) => f.field === filterDefinition.name)
  }, [activeFilters])

  const resetValue = getResetValue({
    filterDefinition,
  })

  if (!matchedFilter) {
    return <></>
  }

  const { value: filterValue } = matchedFilter

  if (filterDefinition.type === SORT_FILTER_TRAIT_VALUE) {
    return matchedFilter.value ? (
      <Chip
        setFilterValue={setFilterValue}
        value={resetValue}
        field={filterDefinition.name}
      >
        {filterDefinition.label}: <>{filterValue}</>
      </Chip>
    ) : (
      <></>
    )
  }
  if (filterDefinition.type === SORT_FILTER_TRAIT_DATERANGE) {
    if (isValidDateRangeFilter(filterValue)) {
      return (
        <Chip
          setFilterValue={setFilterValue}
          value={resetValue}
          field={filterDefinition.name}
        >
          {filterDefinition.label}:{' '}
          <>
            {dayjs(filterValue.startDate).format('DD.MM.YYYY')} -{' '}
            {dayjs(filterValue.endDate).format('DD.MM.YYYY')}
          </>
        </Chip>
      )
    }
  }

  if (filterDefinition.type === SORT_FILTER_TRAIT_MULTIVALUE) {
    if (Array.isArray(filterValue) && filterValue.length > 0) {
      return (
        <Chip
          setFilterValue={setFilterValue}
          value={resetValue}
          field={filterDefinition.name}
        >
          {filterDefinition.label}:{' '}
          <>
            {arraySlicer(
              filterValue
                .map((v: string | number) => {
                  const matchedOption = find(
                    filterDefinition.options,
                    (o: string | SelectItem): o is string | SelectItem => {
                      if (typeof o === 'string') {
                        return o === v.toString()
                      } else {
                        return o.value === v.toString()
                      }
                    },
                  )
                  if (matchedOption) {
                    if (typeof matchedOption === 'string') {
                      return matchedOption
                    } else {
                      return matchedOption.label
                    }
                  }

                  return null
                })
                .filter(isNotNull) as unknown as string[],
              '&',
            )}
          </>
        </Chip>
      )
    }
  }

  if (filterDefinition.type === SORT_FILTER_TRAIT_THREE_SIDED_BOOLEAN) {
    if (
      filterValue === '1' ||
      filterValue === '0' ||
      filterValue === SORT_FILTER_TRAIT_UNRESTRICTED_VALUE
    ) {
      return (
        <Chip
          setFilterValue={setFilterValue}
          value={
            filterValue === SORT_FILTER_TRAIT_UNRESTRICTED_VALUE
              ? null
              : resetValue
          }
          field={filterDefinition.name}
        >
          {filterDefinition.label}:{' '}
          <>
            {filterValue === SORT_FILTER_TRAIT_UNRESTRICTED_VALUE
              ? (filterDefinition.unrestricedLabel ??
                i18n.t('unrestricted_default_label', 'Ohne Einschränkung')) // t('unrestricted_default_label')
              : filterValue === '0'
                ? filterDefinition.falseLabel
                : filterDefinition.trueLabel}
          </>
        </Chip>
      )
    }
  }

  return <></>
}

export default FilterChip

export const Chip = ({
  children,
  value,
  field,
  setFilterValue,
}: {
  children: ReactNode
  value: unknown | null
  field: string
  setFilterValue: UseSortFilter['setActiveFilterValue']
}): React.ReactElement => {
  const theme = useMantineTheme()

  return (
    <Badge
      rightSection={
        value === null ? undefined : (
          <Tooltip
            withinPortal
            //label={t('remove_this_filter', 'Filter entfernen')}>
            label={i18n.t('remove_this_filter', 'Diesen Filter entfernen')}
          >
            <Center>
              <FontAwesomeIcon
                icon={faTimes}
                fontSize={theme.fontSizes.xs}
                cursor="pointer"
                onClick={() => setFilterValue({ field, value })}
              />
            </Center>
          </Tooltip>
        )
      }
    >
      {children}
    </Badge>
  )
}
